import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import DataSummary from '../components/dataSummary';
import Modal from '../components/modal';
import LoadingAnimation from '../components/loadingAnimation';
import '../styles/FTSOSubmissions.css';
import { 
  StatusCellRenderer, 
  NetworkCellRenderer, 
  EntityCellRenderer, 
  HitRateCellRenderer
} from '../utils/metrics';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useStatus } from '../context/StatusContext';
import { getApiBase } from '../utils/apiBase';
import { useDataCache, globalCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';
import { useDataUpdates } from '../hooks/useDataUpdates';
import { StatusEvaluator } from '../context/StatusEvaluator';


const formatPivotData = (data) => {
  const formattedData = [];
  let maxRound = 0;
  
  Object.entries(data).forEach(([entity, networks]) => {
    Object.entries(networks).forEach(([network, rounds]) => {
      Object.entries(rounds).forEach(([roundId, roundData]) => {
        const roundNum = parseInt(roundId, 10);
        maxRound = Math.max(maxRound, roundNum);
        
        formattedData.push({
          voting_round_id: roundNum,
          entity,
          network,
          ...roundData
        });
      });
    });
  });
          
  // Sort and filter to last 6 hours (240 rounds)
  const cutoffRound = maxRound - 240;
  return formattedData
    .filter(row => row.voting_round_id >= cutoffRound)
    .sort((a, b) => b.voting_round_id - a.voting_round_id);
};

function FTSOSubmissions() {
  const { getCachedData } = useDataCache();
  
  // Lazy initial state - hydrate from cache immediately to prevent red flash
  const [rowData, setRowData] = useState([]);
  
  const [summaryDataAll, setSummaryDataAll] = useState(() => {
    try {
      return getCachedData('/submission_summary.json') || {};
    } catch {}
    return {};
  });
  
  const [resultsMetricsAll, setResultsMetricsAll] = useState(() => {
    try {
      const cachedDashboard = getCachedData('/ftso_performance_dashboard.json') || {};
      const rmAll = {};
      Object.keys(cachedDashboard).forEach(entityKey => {
        if (entityKey === 'metadata') return;
        Object.keys(cachedDashboard[entityKey] || {}).forEach(networkKey => {
          const rm = cachedDashboard?.[entityKey]?.[networkKey]?.detailed_metrics?.results_metrics;
          if (rm && typeof rm === 'object') {
            if (!rmAll[entityKey]) rmAll[entityKey] = {};
            rmAll[entityKey][networkKey] = rm;
          }
        });
      });
      return rmAll;
    } catch {}
    return {};
  });
  
  const [submit2PricesDataAll, setSubmit2PricesDataAll] = useState({});
  
  const [feedConfigDataAll, setFeedConfigDataAll] = useState(() => {
    try {
      return getCachedData('/feed_config.json') || {};
    } catch {}
    return {};
  });
  
  const [dashboardGeneralData, setDashboardGeneralData] = useState(() => {
    try {
      return getCachedData('/ftso_performance_dashboard.json') || {};
    } catch {}
    return {};
  });
  
  const [loading, setLoading] = useState(true);
  
  const { updatePageStatus } = useStatus();
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    content: null
  });
  const isInitialLoadRef = useRef(true);

  const computeOverallFromDashboard = useCallback((dashboardData) => {
    try {
      const normalize = (s) => {
        const v = String(s || '').toLowerCase();
        if (v === 'yellow') return 'amber';
        if (v === 'green' || v === 'amber' || v === 'red' || v === 'grey') return v;
        return 'grey';
      };
      const rank = (s) => {
        switch (normalize(s)) {
          case 'green': return 3;
          case 'amber': return 2;
          case 'red': return 1;
          case 'grey':
          default: return 0;
        }
      };
      const tf = '15min';
      const evalBox = (dm) => {
        try {
          const hrObj = dm && dm.hit_rates && dm.hit_rates[tf];
          const rmObj = dm && dm.results_metrics && dm.results_metrics[tf];
          if (!hrObj || !rmObj) return 'grey';
          const hitRate = Number(hrObj.hitRate ?? hrObj.hit_rate ?? hrObj.hitrate);
          const late = Number(rmObj.late || 0);
          const missing = Number(rmObj.missing || 0);
          const invalid = Number(rmObj.invalid || 0);
          const totalLMI = late + missing + invalid;
          let st = 'green';
          if (Number.isFinite(hitRate)) {
            if (hitRate < 25) st = 'red';
            else if (hitRate < 30) st = 'amber';
          }
          if (totalLMI >= 3) st = 'red';
          else if (totalLMI >= 1 && st !== 'red') st = 'amber';
          return st;
        } catch {
          return 'grey';
        }
      };
      const statuses = [];
      const entities = ['UYS', 'ENV'];
      const networks = ['flare', 'songbird'];
      entities.forEach(entity => {
        networks.forEach(network => {
          const dm = dashboardData?.[entity]?.[network]?.detailed_metrics;
          const st = evalBox(dm);
          if (st) statuses.push(normalize(st));
        });
      });
      if (statuses.length === 0) return 'grey';
      return statuses.reduce((best, s) => (rank(s) < rank(best) ? s : best), 'green');
    } catch { return 'grey'; }
  }, []);

  const pivotMapRef = useRef(new Map());
  const handleDataUpdate = useCallback(() => {
    if (isInitialLoadRef.current) {
      setLoading(true);
    }
    const timestamp = Date.now();
    console.log(`handleDataUpdate called at ${new Date(timestamp).toISOString()} (cache-driven)`);

    try {
      const summaryData = globalCache.get('/submission_summary.json') || {};
      const feedCountsData = globalCache.get('/feed_counts.json') || {};
      const submit2PricesData = globalCache.get('/submit2prices.json') || {};
      const feedConfigData = globalCache.get('/feed_config.json') || {};
      const dashboardData = globalCache.get('/ftso_performance_dashboard.json') || {};

      // Fill optional state when available
      setSummaryDataAll(summaryData);
      setSubmit2PricesDataAll(submit2PricesData);
      setFeedConfigDataAll(feedConfigData);
      setDashboardGeneralData(dashboardData);

      // Populate results metrics from dashboard (no client-side computation)
      try {
        const rmAll = {};
        Object.keys(dashboardData || {}).forEach(entityKey => {
          if (entityKey === 'metadata') return;
          Object.keys(dashboardData[entityKey] || {}).forEach(networkKey => {
            const rm = dashboardData?.[entityKey]?.[networkKey]?.detailed_metrics?.results_metrics;
            if (rm && typeof rm === 'object') {
              if (!rmAll[entityKey]) rmAll[entityKey] = {};
              rmAll[entityKey][networkKey] = rm;
            }
          });
        });
        setResultsMetricsAll(rmAll);
      } catch {}

      // Prefer dashboard-derived status when present; fallback to evaluator when all inputs are ready
      const dashStatus = computeOverallFromDashboard(dashboardData);
      if (dashStatus && dashStatus !== 'grey') {
        // Allow upgrades immediately; suppress downgrades during active refresh
        if (!cacheManager.isRefreshing() || dashStatus === 'green') {
          updatePageStatus('ftsoSubmissions', dashStatus);
        }
      }
    } catch (error) {
      console.error('Error in handleDataUpdate (cache path):', error);
      if (!cacheManager.isRefreshing()) {
        updatePageStatus('ftsoSubmissions', 'red');
      }
    } finally {
      setLoading(false);
    }
  }, [updatePageStatus]);
  
  const API = getApiBase();
  const applySnapshot = useCallback((rows, cutoff) => {
    try {
      const m = new Map();
      rows.forEach(r => { if (r && r.id) m.set(r.id, r); });
      pivotMapRef.current = m;
      const arr = Array.from(m.values()).sort((a,b) => b.voting_round_id - a.voting_round_id);
      setRowData(arr);
    } catch {}
  }, []);

  const handlePivotDelta = useCallback((data) => {
    try {
      if (!data || data.type !== 'pivot_delta') return;
      const m = pivotMapRef.current || new Map();
      if (Array.isArray(data.adds)) { data.adds.forEach(r => { if (r && r.id) m.set(r.id, { ...(m.get(r.id) || {}), ...r }); }); }
      if (Array.isArray(data.updates)) { data.updates.forEach(u => { const prev = m.get(u.id) || null; if (prev) { m.set(u.id, { ...prev, ...(u.changes || {}) }); } }); }
      if (Array.isArray(data.removes)) { data.removes.forEach(id => { m.delete(id); }); }
      const cut = data.cutoff || {};
      if (cut && (cut.flare || cut.songbird)) {
        for (const [id, row] of Array.from(m.entries())) {
          const co = cut[row.network] || 0;
          if (Number(row.voting_round_id) < co) m.delete(id);
        }
      }
      pivotMapRef.current = m;
      const arr = Array.from(m.values()).sort((a,b) => b.voting_round_id - a.voting_round_id);
      setRowData(arr);
    } catch {}
  }, []);

  useDataUpdates(handleDataUpdate, { onPivotDelta: handlePivotDelta });
  
  useEffect(() => {
    cacheManager.registerEndpoints('ftsoSubmissions', [
      '/submission_summary.json',
      '/feed_counts.json',
      '/feed_config.json',
      '/ftso_performance_dashboard.json'
    ]);
    
    console.log('Component mounted, performing initial data fetch');
    try {
      const cachedSummary = getCachedData('/submission_summary.json') || {};
      const cachedCounts = getCachedData('/feed_counts.json') || {};
      const cachedSubmit2 = {};
      const cachedFeedCfg = getCachedData('/feed_config.json') || {};
      const cachedDashboard = getCachedData('/ftso_performance_dashboard.json') || {};
      if (Object.keys(cachedDashboard).length > 0) {
        setSummaryDataAll(cachedSummary);
        // Prefer dashboard results metrics when present
        const rmAll = {};
        Object.keys(cachedDashboard || {}).forEach(entityKey => {
          if (entityKey === 'metadata') return;
          Object.keys(cachedDashboard[entityKey] || {}).forEach(networkKey => {
            const rm = cachedDashboard?.[entityKey]?.[networkKey]?.detailed_metrics?.results_metrics;
            if (rm && typeof rm === 'object') {
              if (!rmAll[entityKey]) rmAll[entityKey] = {};
              rmAll[entityKey][networkKey] = rm;
            }
          });
        });
        setResultsMetricsAll(rmAll);
        setSubmit2PricesDataAll(cachedSubmit2);
        setFeedConfigDataAll(cachedFeedCfg);
        setDashboardGeneralData(cachedDashboard);
        // Prefer dashboard-derived status when present; fallback to evaluator when all inputs are ready
        const dashStatus = computeOverallFromDashboard(cachedDashboard);
        if (dashStatus && dashStatus !== 'grey') {
          updatePageStatus('ftsoSubmissions', dashStatus);
        }
      }
    } catch {}
    ;(async () => {
      try {
        const resp = await fetch(`${API}/api/pivot/snapshot?window=240`);
        if (resp.ok) {
          const snap = await resp.json();
          if (snap && Array.isArray(snap.rows)) {
            applySnapshot(snap.rows, snap.cutoff || {});
          }
        }
      } catch {}
      handleDataUpdate();
      setLoading(false);
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        console.log('Initial load process complete, isInitialLoadRef set to false.');
      }
    })();
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      console.log('Initial load process complete, isInitialLoadRef set to false.');
    }
    
    return () => {
      console.log('Component unmounting, cleaning up state');
      setRowData([]);
      setSummaryDataAll({});
      setResultsMetricsAll({});
      setSubmit2PricesDataAll({});
      setFeedConfigDataAll({});
      setDashboardGeneralData({});
      isInitialLoadRef.current = true;
      cacheManager.unregisterEndpoints('ftsoSubmissions');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [handleDataUpdate]);
  

  const handleZeroSubsClick = (entity, network, timeframe, zeroRounds) => {
    console.log('Opening modal with:', { entity, network, timeframe, zeroRounds });
    setModalState(prevState => {
      const newState = {
        isOpen: true,
        title: `Zero Submissions - ${entity} ${network} (${timeframe})`,
        content: (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Voting rounds with zero submissions:
            </p>
            <div className="grid grid-cols-4 gap-2">
              {zeroRounds.map(round => (
                <div key={round} className="bg-gray-100 rounded p-2 text-center text-black">
                  {round}
                </div>
              ))}
            </div>
          </div>
        )
      };
      console.log('New modal state:', newState); // Debug log
      return newState;
    });
  };

  // Reveal offenders interactions removed

  // Compute a missing per-round hit_rate on the fly using cached submit2prices + feed_config
  const computeHitRateForRow = useCallback((row) => {
    try {
      if (!row || row.hit_rate != null) return null;
      if (String(row.Results).toUpperCase() !== 'RECEIVED') return null;
      const entity = row.entity;
      const netKey = (row.network || '').toString().toLowerCase();
      const roundStr = String(row.voting_round_id);
      const submit2ForRound = submit2PricesDataAll?.[entity]?.[netKey]?.[roundStr];
      const feedsCfg = Array.isArray(feedConfigDataAll?.[entity]?.[netKey]) ? feedConfigDataAll[entity][netKey] : [];
      if (!Array.isArray(submit2ForRound) || feedsCfg.length === 0) return null;
      const expectedFeeds = feedsCfg
        .filter(f => f && f.submit === true && typeof f.feed_name === 'string')
        .map(f => f.feed_name.trim().toLowerCase());
      if (expectedFeeds.length === 0) return null;
      const resultsMap = {};
      submit2ForRound.forEach(item => {
        const k = (item?.feed || '').toString().trim().toLowerCase();
        if (k) resultsMap[k] = Number(item?.result);
      });
      let total = 0; let observed = 0;
      expectedFeeds.forEach(f => {
        total += 1;
        const v = resultsMap[f];
        if (typeof v === 'number' && !Number.isNaN(v)) observed += v;
      });
      if (total === 0) return null;
      const pct = (observed / total) * 100;
      return Math.round(pct);
    } catch { return null; }
  }, [submit2PricesDataAll, feedConfigDataAll]);

  const hasPricesForRound = useCallback((row) => {
    try {
      const entity = row.entity;
      const netKey = (row.network || '').toString().toLowerCase();
      const roundStr = String(row.voting_round_id);
      const feeds = submit2PricesDataAll?.[entity]?.[netKey]?.[roundStr];
      if (!Array.isArray(feeds) || feeds.length === 0) return false;
      return feeds.some(f => typeof f?.result === 'number' && Number.isFinite(f.result));
    } catch { return false; }
  }, [submit2PricesDataAll]);

  const hasPositivePriceResultForRound = useCallback((row) => {
    try {
      const entity = row.entity;
      const netKey = (row.network || '').toString().toLowerCase();
      const roundStr = String(row.voting_round_id);
      const feeds = submit2PricesDataAll?.[entity]?.[netKey]?.[roundStr];
      if (!Array.isArray(feeds) || feeds.length === 0) return false;
      return feeds.some(f => typeof f?.result === 'number' && Number.isFinite(f.result) && Number(f.result) > 0);
    } catch { return false; }
  }, [submit2PricesDataAll]);

  // Patch rows in-place when we have data to fill holes; minimal work only for rows with null hit_rate
  useEffect(() => {
    setRowData(prev => {
      try {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        let changed = false;
        const next = prev.map(row => {
          if (row && (row.hit_rate == null)) {
            const v = computeHitRateForRow(row);
            if (v != null) { changed = true; return { ...row, hit_rate: v, observed_count: row.observed_count ?? undefined, expected_count: row.expected_count ?? undefined }; }
          }
          return row;
        });
        return changed ? next : prev;
      } catch { return prev; }
    });
  }, [computeHitRateForRow, rowData, submit2PricesDataAll, feedConfigDataAll]);

  // Compute latest and second-latest VR per network for grace display rules
  const vrWindowByNetwork = useMemo(() => {
    try {
      const byNet = {};
      rowData.forEach(r => {
        const n = (r.network || '').toString();
        const vr = Number(r.voting_round_id);
        if (!byNet[n]) byNet[n] = new Set();
        if (Number.isFinite(vr)) byNet[n].add(vr);
      });
      const latest = {}; const second = {};
      Object.keys(byNet).forEach(n => {
        const sorted = Array.from(byNet[n]).sort((a, b) => b - a);
        if (sorted.length > 0) latest[n] = sorted[0];
        if (sorted.length > 1) second[n] = sorted[1];
      });
      return { latest, second };
    } catch { return { latest: {}, second: {} }; }
  }, [rowData]);

  const columnDefs = [
    { 
      field: 'voting_round_id', 
      headerName: 'Round', 
      minWidth: 120, 
      maxWidth: 130, 
      flex: 1.2, 
      sortable: true, 
      valueParser: params => Number(params.newValue),
    },
    { 
      field: 'entity', 
      headerName: 'Entity', 
      sortable: true, 
      filter: true, 
      minWidth: 120, 
      maxWidth: 200, 
      flex: 1.2, 
      cellRenderer: EntityCellRenderer 
    },
    { 
      field: 'network', 
      headerName: 'Network', 
      cellRenderer: NetworkCellRenderer, 
      minWidth: 140, 
      maxWidth: 200, 
      flex: 1.3 
    },
    { 
      field: 'end_timeTime', 
      headerName: 'Round End', 
      sortable: true, 
      filter: true, 
      minWidth: 160, 
      maxWidth: 180, 
      flex: 1.5, 
      sort: 'desc', 
      sortingOrder: ['desc', 'asc'],
      valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleString('en-GB', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone: 'UTC'
        }).replace(',', '');
      }
    },
    { 
      field: 'submit1', 
      headerName: 'Submit 1', 
      sortable: true, 
      flex: 1, 
      cellRenderer: StatusCellRenderer 
    },
    { 
      field: 'submit2', 
      headerName: 'Submit 2', 
      sortable: true, 
      minWidth: 120,
      flex: 1, 
      cellRenderer: StatusCellRenderer,
      valueGetter: params => params.data?.submit2
    },
    { 
      field: 'submitSignatures', 
      headerName: 'Signatures', 
      sortable: true, 
      minWidth: 120, 
      flex: 1, 
      cellRenderer: StatusCellRenderer 
    },
    { 
      field: 'Results', 
      headerName: 'Results', 
      sortable: true, 
      filter: true, 
      minWidth: 120, 
      flex: 1, 
      cellRenderer: StatusCellRenderer 
    },
    { 
      field: 'hit_rate', 
      headerName: 'Hit Rate', 
      sortable: true, 
      filter: true, 
      minWidth: 90, 
      flex: 1, 
      cellRenderer: HitRateCellRenderer,
      valueGetter: params => {
        try {
          const results = params.data.Results;
          if (String(results || '').toUpperCase() === 'PENDING') return null;
          let hr = params.data.hit_rate;
          // If hr is zero but we have no prices parsed for the round yet,
          // treat as pending until submit2prices arrives
          if (hr === 0 && !hasPricesForRound(params.data)) {
            return null;
          }
          // If hr is zero and we don't have any positive price outcomes yet,
          // also treat as pending (calculation likely not finished)
          if (hr === 0 && !hasPositivePriceResultForRound(params.data)) {
            return null;
          }
          // Grace window: for latest or second-latest VR per network, hide zero until finalized
          const net = (params.data.network || '').toString();
          const vr = Number(params.data.voting_round_id);
          const isGrace = vr === vrWindowByNetwork.latest[net] || vr === vrWindowByNetwork.second[net];
          if (hr === 0 && isGrace) {
            return null;
          }
          if ((hr === undefined || hr === null) && results === 'RECEIVED') {
            const computed = computeHitRateForRow(params.data);
            if (computed != null) {
              // mutate row data minimally so later renders have the value
              params.data.hit_rate = computed;
              hr = computed;
            } else {
              return null;
            }
          }
          return hr;
        } catch { return params.data.hit_rate; }
      }
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
  };

  const getRowId = useCallback((params) => {
    return `${params.data.entity}-${params.data.network}-${params.data.voting_round_id}`;
  }, []);

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <div className="main-wrapper">
      <DataSummary 
        data={summaryDataAll}
        resultsMetricsAll={resultsMetricsAll}
        submit2PricesDataAll={submit2PricesDataAll}
        feedConfigDataAll={feedConfigDataAll}
        dashboardGeneralData={dashboardGeneralData}
        onZeroSubsClick={handleZeroSubsClick}
      />
      <div className="grid-wrapper">
        <div className="ag-theme-alpine-dark custom-dark-theme">
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            suppressHorizontalScroll={false}
            rowHeight={45}
            headerHeight={50}
            getRowId={getRowId}
            pagination={true}
            paginationPageSize={500}
            paginationNumberFormatter={function(params) {
              return '[' + params.value.toLocaleString() + ']';
            }}
            paginationAutoPageSize={false}
          />
        </div>
      </div>
  
      <div className="absolute inset-0" style={{ position: 'fixed', zIndex: 9999 }}>
        <Modal
          isOpen={modalState.isOpen}
          onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
          title={modalState.title}
        >
          {modalState.content}
        </Modal>
      </div>
    </div>
  );
  
}

export default FTSOSubmissions;